import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Home, Wallet, Terminal, ShoppingCart, ChevronDown } from 'lucide-react';
import { getMerchantPaymentSettings, getUserInfo } from '../../services/useAPICaller';
import { PaymentSettings } from '../../types/payment.types';
import ProfileSettingsMenu from '../ui/ProfileSettingsMenu';
import { useI18n } from '../../i18n/I18n';

export default function Sidebar() {
    const { t } = useI18n();
    const location = useLocation();
    const history = useHistory();
    const path = location.pathname;

    const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
    const [userProfile, setUserProfile] = useState<{ pictureUrl?: string; name?: string } | null>(null);
    const [advancedOpen, setAdvancedOpen] = useState(true);

    const isActive = (route: string) => path.includes(route);

    useEffect(() => {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;

        getMerchantPaymentSettings((data) => {
            setPaymentSettings(data);
        });

        getUserInfo().then(info => {
            if (info) setUserProfile(info);
        }).catch(() => { });
    }, []);

    const profileImageSrc = userProfile?.pictureUrl || paymentSettings?.merchantLogoUrl;

    const mainNav = useMemo(() => [
        { route: '/admin/dashboard', icon: Home, label: 'Dashboard' },
        { route: '/admin/pos', icon: Plus, label: t('pos.title') },
        { route: '/admin/settings', icon: Wallet, label: t('settings.title') },
    ], [t]);

    const advancedNav = useMemo(() => [
        { route: '/admin/developers', icon: Terminal, label: t('developers.title') },
        { route: '/admin/woocommerce', icon: ShoppingCart, label: t('notifications.woocommerce.title') },
    ], [t]);

    const handleLogout = () => {
        localStorage.removeItem('jwtToken');
        window.location.reload();
    };

    // Footer profile logic
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

    return (
        <>
            <aside className="hidden lg:flex flex-col w-[272px] min-w-[272px] h-screen border-r z-30 select-none flex-shrink-0"
                style={{ background: 'var(--nav-bg)', borderColor: 'var(--nav-border)' }}
            >
                {/* Brand */}
                <div className="px-7 pt-8 pb-6 flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-electric-blue flex-shrink-0"></div>
                    <span className="text-[22px] font-bold text-txt-heading tracking-tight leading-none">Sprint</span>
                </div>

                {/* Main Navigation */}
                <nav className="flex-1 px-4 space-y-1 overflow-y-auto no-scrollbar">
                    <p className="text-[10px] font-bold text-txt-muted uppercase tracking-widest px-3 mb-2">General</p>
                    {mainNav.map(({ route, icon: Icon, label }) => {
                        const active = isActive(route);
                        return (
                            <button
                                key={route}
                                onClick={() => history.push(route)}
                                className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer border-none ${active
                                    ? 'text-white'
                                    : 'text-txt-secondary hover:text-txt-heading hover:bg-surface-hover'
                                    }`}
                                style={!active ? { background: 'transparent' } : undefined}
                            >
                                {active && (
                                    <motion.div
                                        layoutId="sidebar-active"
                                        className="absolute inset-0 rounded-xl bg-electric-blue"
                                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                        style={{ zIndex: -1 }}
                                    />
                                )}
                                <Icon size={18} strokeWidth={active ? 2.5 : 2} className="relative z-10 flex-shrink-0" />
                                <span className="relative z-10 truncate">{label}</span>
                            </button>
                        );
                    })}

                    {/* Advanced Section */}
                    <div className="pt-5">
                        <button
                            onClick={() => setAdvancedOpen(!advancedOpen)}
                            className="w-full flex items-center justify-between px-3 py-1 mb-2 bg-transparent border-none cursor-pointer"
                        >
                            <p className="text-[10px] font-bold text-txt-muted uppercase tracking-widest">Advanced</p>
                            <ChevronDown
                                size={14}
                                className={`text-txt-muted transition-transform duration-200 ${advancedOpen ? 'rotate-0' : '-rotate-90'}`}
                            />
                        </button>
                        {advancedOpen && advancedNav.map(({ route, icon: Icon, label }) => {
                            const active = isActive(route);
                            return (
                                <button
                                    key={route}
                                    onClick={() => history.push(route)}
                                    className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer border-none ${active
                                        ? 'text-white'
                                        : 'text-txt-secondary hover:text-txt-heading hover:bg-surface-hover'
                                        }`}
                                    style={!active ? { background: 'transparent' } : undefined}
                                >
                                    {active && (
                                        <motion.div
                                            layoutId="sidebar-active"
                                            className="absolute inset-0 rounded-xl bg-electric-blue"
                                            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                            style={{ zIndex: -1 }}
                                        />
                                    )}
                                    <Icon size={18} strokeWidth={active ? 2.5 : 2} className="relative z-10 flex-shrink-0" />
                                    <span className="relative z-10 truncate">{label}</span>
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* Footer Component */}
                <div className="px-4 pb-6 pt-4 space-y-2 mt-auto" style={{ borderTop: '1px solid var(--divider)' }}>
                    {paymentSettings && (
                        <button
                            onClick={() => setIsProfileMenuOpen(true)}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors cursor-pointer border border-transparent hover:border-surface-border text-left"
                            style={{ background: 'var(--color-surface)' }}
                        >
                            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center relative"
                                style={{ background: 'var(--color-surface-hover)', border: '1px solid var(--color-surface-border)' }}
                            >
                                {profileImageSrc ? (
                                    <img src={profileImageSrc} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                ) : (
                                    <span className="material-symbols-outlined text-txt-heading text-[18px]">person</span>
                                )}
                                {userProfile && isProfileMenuOpen && (
                                    <div className="absolute bottom-[-2px] right-[-2px] w-[14px] h-[14px] bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100">
                                        <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" className="w-[8px] h-[8px] object-contain" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-txt-heading truncate leading-tight">
                                    {userProfile?.name || paymentSettings.merchantName || 'Usuario'}
                                </p>
                                <p className="text-[11px] text-txt-muted truncate">
                                    {paymentSettings.merchantName || paymentSettings.mail || ''}
                                </p>
                            </div>
                            <div className="flex-shrink-0 text-txt-muted group-hover:text-txt-heading flex items-center">
                                <span className="material-symbols-outlined text-[18px]">settings</span>
                            </div>
                        </button>
                    )}
                </div>
            </aside>

            {/* Profile Settings Modal Overlay */}
            <ProfileSettingsMenu
                isOpen={isProfileMenuOpen}
                onClose={() => setIsProfileMenuOpen(false)}
                paymentSettings={paymentSettings}
                userProfile={userProfile}
                onLogout={handleLogout}
            />
        </>
    );
}
